import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PATHS } from '@belong/common';
import { Text } from '@belong/ui';
import { getLeaseSignaturesStatus } from 'accounts/utils/leases';
import { UserModel } from 'api/models';
import classNames from 'classnames/bind';
import ColoredText, { COLORS } from 'components/ColoredText/ColoredText';
import Divider from 'components/Divider/Divider';
import Label from 'components/Label/Label';
import { LABEL_COLOR_TYPES } from 'components/Label/labelTypes';
import { TourHostTile } from 'components/tour-host-tile/tour-host-tile';
import ResidentListContainer from 'containers/ResidentListContainer/ResidentListContainer';
import Space from 'corecomponents/Space/Space';
import { Box, Flex, Timeline } from 'design-system';
import { startCase } from 'es-toolkit/compat';
import { useModal } from 'hooks/useModal';
import {
  ApplicantStatus,
  ApplicationStatus,
  DisplayStatus,
  LeaseStatus,
  ListingApplicationStatus,
  UnitVisitType,
} from 'models/enums';
import RenterAccountBookmark from 'models/renterAccounts/RenterAccountBookmark';
import { ApplicationsInProcess } from 'pages/Accounts/Pages/ToursAndApps/ApplicationsInProcess/ApplicationsInProcess';
import { PlaceInLine } from 'pages/Accounts/Pages/ToursAndApps/PlaceInLine/PlaceInLine';
import { BASE_PATHS } from 'routes/paths';
import {
  cancelSelfTour,
  cancelPrivateTour,
  fetchResidentBookmarks,
  updateCancelResidentBookmarkById,
} from 'store/redux/renter-accounts/actions';
import { selectLeaseSignatureInformation } from 'store/redux/renter-accounts/selectors';
import { validateUserInterestSurveyEntry } from 'store/redux/user/actions';
import { arrayInsertIf } from 'utils/insertIf';
import BDate, { DATE_TYPES } from '../../components/BDate/BDate';
import styles from './TourAndAppsCard.module.css';
import {
  TourCard,
  TourCardActions,
  TourCardTitleWithDetails,
} from './TourCard';
import { WithdrawModal } from './WithdrawModal';

const cx = classNames.bind(styles);

const getTimelineSteps = (
  currentResidentId: any,
  applicants: any,
  isCosigner: any,
  applicationGroupHasTouredHome: any,
  leaseStatus: any,
) => {
  const otherResidents = applicants.filter(
    (applicant: any) => applicant.residentId !== currentResidentId,
  );
  return [
    {
      value: 'Submit Your Application',
      checked: true,
    },
    ...otherResidents.map((applicant: any) => {
      const { firstName, status } = applicant;
      const checked =
        status === ApplicantStatus.Submitted ||
        status === ApplicantStatus.VerificationsCompleted;
      return {
        value: `Receive ${firstName}'s Application`,
        checked,
      };
    }),
    ...arrayInsertIf(!isCosigner, {
      value: 'Tour The Home',
      checked: applicationGroupHasTouredHome,
    }),
    {
      value: 'Get Approved',
      checked:
        leaseStatus === LeaseStatus.Offered ||
        leaseStatus === LeaseStatus.ResidentsSigned,
    },
    {
      value: 'Become A Belong Resident',
      checked: false,
    },
  ];
};

const getStatus = (bookmark: any) => {
  if (bookmark.displayStatus === DisplayStatus.ScheduledTour) {
    if (bookmark.tourType === UnitVisitType.SelfTour) {
      return 'Self Tour';
    }
    if (bookmark.tourType === UnitVisitType.PrivateTour) {
      return 'Private Tour';
    }

    if (bookmark.isOccupied) {
      return 'Occupied Group Tour';
    }

    return 'Group Tour';
  }

  if (bookmark.displayStatus === DisplayStatus.CanceledTour) {
    return 'Canceled Tour';
  }
};

const getTourTagColor = (bookmark: any) => {
  const { tourType, displayStatus, hasPaymentHold } = bookmark;

  if (
    displayStatus === DisplayStatus.ScheduledTour &&
    !hasPaymentHold &&
    tourType === UnitVisitType.SelfTour
  ) {
    return COLORS.RED;
  }

  switch (displayStatus) {
    case DisplayStatus.ScheduledTour:
      return COLORS.GREEN;
    case DisplayStatus.Applied:
      return COLORS.GREEN;
    case DisplayStatus.CanceledTour:
      return COLORS.DARKGRAY;
    // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
    case DisplayStatus.Rejected:
      return COLORS.RED;
    default:
      return COLORS.NAVY;
  }
};

// `LABEL_COLOR_TYPES` rather than `ColoredText`'s `COLORS`, which is what feeds
// `Label` here. Both spell these four colours identically, so nothing renders
// differently — but `COLORS` is a plain JS object, so its members widen to
// `string` and cannot satisfy `Label`'s `LabelColorType`, while
// `LABEL_COLOR_TYPES` is `as const` and is where that type comes from.
const getListingApplicationStatusColorConfig = (status: any) => {
  switch (status) {
    case ListingApplicationStatus.Withdrawn:
      return LABEL_COLOR_TYPES.GRAY;
    case ListingApplicationStatus.Approved:
      return LABEL_COLOR_TYPES.GREEN;
    case ListingApplicationStatus.CoSignerRequired:
    case ListingApplicationStatus.Rejected:
      return LABEL_COLOR_TYPES.RED;
    case ListingApplicationStatus.None:
    case ListingApplicationStatus.Qualified:
    default:
      return LABEL_COLOR_TYPES.NAVY;
  }
};

const getListingApplicationStatusMapping = (status: any) => {
  switch (status) {
    // TODO: Figure out what to do with this after we fix the verifications
    // case ListingApplicationStatus.CoSignerRequired:
    // return 'Cosigner Required';
    case ListingApplicationStatus.Rejected:
    case ListingApplicationStatus.NotOffered:
      return 'Not Approved';
    case ListingApplicationStatus.None:
    case ListingApplicationStatus.Qualified:
    case ListingApplicationStatus.NotQualified:
    case ListingApplicationStatus.CoSignerRequired:
      return 'Applied';
    case ListingApplicationStatus.ApplicationStarted:
      return 'Application Started';
    default:
      return startCase(status);
  }
};

// Same reason as `getListingApplicationStatusColorConfig` above.
const getLeaseStatusColorConfig = (status: any) => {
  switch (status) {
    case LeaseStatus.Offered:
      return LABEL_COLOR_TYPES.GREEN;
    default:
      return LABEL_COLOR_TYPES.NAVY;
  }
};

const getLeaseStatusMapping = (status: any) => {
  switch (status) {
    case LeaseStatus.Offered:
      return 'Lease Offered';
    case LeaseStatus.ResidentsSigned:
      return 'Interview Pending';
    default:
      return startCase(status);
  }
};

const getLabels = (bookmark: any, isVerified: any) => {
  const {
    tourType,
    displayStatus,
    hasPaymentHold,
    leaseInfo,
    listingApplication,
  } = bookmark;

  const labels = [];
  if (leaseInfo) {
    const leaseStatus = leaseInfo.basicInfo.status;
    labels.push(
      <Label
        className="uppercase"
        text={getLeaseStatusMapping(leaseStatus)}
        color={getLeaseStatusColorConfig(leaseStatus)}
      />,
    );
  } else if (listingApplication) {
    labels.push(
      <Label
        text={getListingApplicationStatusMapping(displayStatus)}
        color={getListingApplicationStatusColorConfig(
          listingApplication.status,
        )}
      />,
    );
  }

  if (
    tourType === UnitVisitType.SelfTour &&
    displayStatus === DisplayStatus.ScheduledTour
  ) {
    if (!isVerified) {
      labels.push(<Label text="ID Unverified" color={LABEL_COLOR_TYPES.RED} />);
    }

    if (!hasPaymentHold) {
      <Label text="Credit Card Missing" color={LABEL_COLOR_TYPES.RED} />;
    }
  }

  return labels.length > 0 ? <div>{labels}</div> : null;
};

type Props = {
  bookmark: RenterAccountBookmark;
  user: UserModel;
  isIdentityVerified: boolean;
};

const TourAndApps = ({ bookmark, isIdentityVerified, user }: Props) => {
  const dispatch = useDispatch();
  const leasesSignatureInformation = useSelector(
    selectLeaseSignatureInformation,
  );

  const [isWithdrawModalOpen, openWithdrawModal, hideWithdrawModal] =
    useModal(false);
  const [isValid, setIsValid] = useState(false);

  const {
    tourId,
    attendeeStatus,
    attendeeId,
    tourType,
    rentAmount,
    unit,
    displayStatus,
    applicationId,
    applicationStatus,
    hasPaymentHold,
    listingApplication,
    listing,
    leaseInfo,
    applicants,
    applicationGroupHasTouredHome,
    isOccupied,
    bookedAttendeesAmount,
    host,
  } = bookmark;

  const { basicInfo } = unit;
  const leaseStatus = leaseInfo?.basicInfo?.status;
  const signatureInformation = leasesSignatureInformation?.find(
    (lease: any) => lease.leaseId === leaseInfo?.basicInfo?.leaseId,
  )?.signatureInfo;

  const { coSigners = [], coApplicants = [], mainApplicant } = applicants || {};

  const allApplicants = mainApplicant
    ? [mainApplicant, ...coSigners, ...coApplicants]
    : [];
  const currentApplicant = allApplicants.find(
    (applicant) => applicant.residentId === user.userId,
  );
  const isMainApplicant = mainApplicant?.residentId === user.userId;
  const isCosigner = !!coSigners?.find(
    (coSigner: any) => coSigner.residentId === user.userId,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(validateUserInterestSurveyEntry(attendeeId));
        setIsValid(true);
      } catch (e) {
        setIsValid(false);
      }
    };

    if (tourType) {
      fetchData();
    }
  }, [attendeeId, tourType, dispatch]);

  function getMainCTA() {
    if (leaseStatus === LeaseStatus.ResidentsSigned) return;

    if (
      signatureInformation?.signMode === 'External' &&
      signatureInformation?.hasResidentCompletedSigningFlow
    ) {
      return {
        customContent: <></>,
      };
    }

    if (leaseStatus === LeaseStatus.Offered) {
      const { signaturesByUserId, isPrimaryResidentSignaturePending } =
        getLeaseSignaturesStatus(leaseInfo);
      // Indexing with an absent `userId` read the literal `"undefined"` key, which
      // this map never has — so this resolves the same way it always did.
      const currentUserSignatureStatus = user.userId
        ? signaturesByUserId[user.userId]
        : undefined;

      if (
        currentUserSignatureStatus &&
        currentUserSignatureStatus.signatureStatus === 'Pending'
      ) {
        if (
          currentUserSignatureStatus.residentType !== 'Primary' &&
          isPrimaryResidentSignaturePending
        ) {
          return {
            customContent: (
              <span className="text-dark-gray">
                Waiting for Primary Resident Signed Lease
              </span>
            ),
          };
        }

        return {
          href: `${PATHS.LEASE_SIGNING_FLOW_V2}/GetStarted?useLatestStep=true`,
          label: 'SIGN LEASE',
        };
      }
    }

    if (
      !applicationGroupHasTouredHome &&
      !isCosigner &&
      displayStatus !== DisplayStatus.ScheduledTour
    ) {
      return {
        label: 'TOUR HOME',
        href: `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours`,
      };
    }

    // Don't show CTA if the user has applied and has attended the tour
    if (
      displayStatus === 'Applied' &&
      currentApplicant &&
      (currentApplicant.status === ApplicantStatus.Submitted ||
        currentApplicant.status === ApplicantStatus.VerificationsCompleted) &&
      (attendeeStatus === 'Booked' || attendeeStatus === 'Attended')
    ) {
      return;
    }

    // Don't show CTA if the user has applied
    if (
      displayStatus === 'Applied' &&
      currentApplicant &&
      (currentApplicant.status === ApplicantStatus.Submitted ||
        currentApplicant.status === ApplicantStatus.VerificationsCompleted)
    ) {
      return;
    }

    if (attendeeStatus === 'Invited') {
      // TODO: Use variable
      if (
        isOccupied &&
        bookedAttendeesAmount >= 10 &&
        tourType !== UnitVisitType.PrivateTour
      )
        return {
          label: 'Reschedule Tour',
          href: `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours`,
        };

      return {
        label: 'Confirm Spot',
        href: `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours`,
      };
    }
  }

  const getActionsArray = () => {
    const actionsArray = [];

    if (
      currentApplicant &&
      currentApplicant.status !== ApplicantStatus.Submitted &&
      currentApplicant.status !== ApplicantStatus.VerificationsCompleted
    ) {
      actionsArray.push({
        label: 'COMPLETE APPLICATION',
        href: (isMainApplicant
          ? PATHS.RESIDENT_APPLICATION_LEASE_STEP
          : PATHS.RESIDENT_APPLICATION_PRE_APPLICANT_FLOW
        ).concat(`?applicationId=${applicationId}&homeId=${basicInfo?.unitId}`),
      });
    }

    if (
      isMainApplicant &&
      currentApplicant &&
      currentApplicant.status === ApplicantStatus.Submitted &&
      ((coApplicants.lenght > 0 &&
        coApplicants.every(
          (coSigner: any) =>
            // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
            coSigner.status === ApplicantStatus.isInvited,
        )) ||
        (coSigners.lenth > 0 &&
          coSigners.every(
            (coSigner: any) =>
              // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
              coSigner.status === ApplicantStatus.isInvited,
          )))
    ) {
      actionsArray.push({
        label: 'EDIT APPLICATION',
        href: PATHS.RESIDENT_APPLICATION_LEASE_STEP.concat(
          `?applicationId=${applicationId}&homeId=${basicInfo?.unitId}`,
        ),
      });
    }

    if (
      currentApplicant &&
      listingApplication &&
      listingApplication.status === ListingApplicationStatus.CoSignerRequired &&
      !isCosigner
    ) {
      actionsArray.push({
        label: 'ADD Co-Signer',
        href: PATHS.RESIDENT_APPLICATION_CO_SIGNER_STEP.concat(
          `?applicationId=${applicationId}&homeId=${basicInfo?.unitId}`,
        ),
      });
    }

    if (
      !leaseInfo &&
      isMainApplicant &&
      applicationStatus === ApplicationStatus.Submitted
    ) {
      actionsArray.push({
        label: 'WITHDRAW APPLICATION',
        onClick: openWithdrawModal,
      });
    }

    if (tourType) {
      if (
        isValid &&
        displayStatus === DisplayStatus.CompletedTour &&
        leaseStatus !== LeaseStatus.Offered &&
        leaseStatus !== LeaseStatus.ResidentsSigned
      ) {
        actionsArray.push({
          label: 'UPDATE TOUR INTEREST',
          href: `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=survey&token=${attendeeId}&attendance=${attendeeStatus}`,
        });
      }

      if (
        tourType !== UnitVisitType.PrivateTour &&
        displayStatus === DisplayStatus.ScheduledTour &&
        !hasPaymentHold
      ) {
        actionsArray.push({
          label: 'RESCHEDULE',
          href: `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours&step=reschedule`,
        });
      }

      if (displayStatus === DisplayStatus.ScheduledTour) {
        actionsArray.push({
          label: 'CANCEL TOUR',
          onClick: async () => {
            try {
              if (tourType === UnitVisitType.OpenHome) {
                await dispatch(updateCancelResidentBookmarkById(tourId));
              } else if (tourType === UnitVisitType.PrivateTour) {
                await dispatch(cancelPrivateTour(tourId));
              } else {
                await dispatch(cancelSelfTour(tourId));
              }
              await dispatch(fetchResidentBookmarks());
            } catch (error) {
              console.error(error);
            }
          },
        });
      }
    }

    return actionsArray;
  };

  const actions = getActionsArray();
  const mainCta = getMainCTA();

  const showDates =
    displayStatus === DisplayStatus.ScheduledTour ||
    displayStatus === DisplayStatus.CanceledTour;

  const showBottom = allApplicants.some((applicant) => {
    const { status } = applicant || {};
    return (
      status === ApplicantStatus.Submitted ||
      status === ApplicantStatus.VerificationsCompleted
    );
  });

  const isPrequalified =
    applicationStatus === ApplicationStatus.VerificationsCompleted &&
    listingApplication?.status === ListingApplicationStatus.Qualified;

  const placeInLine = listingApplication?.waitlistRanking;

  return (
    <>
      <TourCard
        homeUniqueId={basicInfo.unitId}
        imageUrl={unit.basicInfo.bannerImageUrl}
        topContent={
          <>
            <div className="mb-xs flex gap-xs">
              {getLabels(bookmark, isIdentityVerified)}
            </div>
            <div className={cx('visitorStatus')}>
              <ColoredText
                text={
                  <>
                    <Text variant="p1" as="span" fontWeight="semibold">
                      {getStatus(bookmark)}
                    </Text>
                    {showDates && (
                      <>
                        &nbsp;|&nbsp;
                        <BDate
                          time={bookmark.visitStartOn}
                          formatType={DATE_TYPES.DOTTED_LONG_WITH_SPACE}
                          timeZone={unit?.basicInfo?.timeZone}
                          withTimeZone
                        />
                        &nbsp;-&nbsp;
                        <BDate
                          time={bookmark.visitEndOn}
                          formatType={DATE_TYPES.TIME}
                          timeZone={unit?.basicInfo?.timeZone}
                          withTimeZone
                        />
                      </>
                    )}
                  </>
                }
                color={getTourTagColor(bookmark)}
              />
            </div>
            <TourCardTitleWithDetails
              title={bookmark.address.getAddressAndUnitNumber(
                unit.basicInfo.unitNumber,
              )}
              basicInfo={basicInfo}
              rentAmount={rentAmount}
            />
            {host && <TourHostTile {...host} />}
          </>
        }
        bottomContent={
          <>
            {(mainCta || actions.length > 0) && (
              <TourCardActions
                actions={[
                  ...actions,
                  ...(mainCta && typeof mainCta === 'object'
                    ? [
                        {
                          ...mainCta,
                          isCTA: true,
                        },
                      ]
                    : []),
                ]}
              />
            )}
            {showBottom ? (
              <>
                <Space.LG />
                <Divider />
                <Space.LG />

                <Flex
                  flexDirection={['column-reverse', 'column-reverse', 'row']}
                >
                  <Box
                    flex={1}
                    px={['2sm', '2sm', 0]}
                    py={['2xl', '2xl', 0]}
                    className={cx('timeline-container')}
                  >
                    <Box mb="sm">
                      <Text fontWeight="semibold">What&apos;s Next?</Text>
                    </Box>
                    <Timeline
                      items={getTimelineSteps(
                        user.userId,
                        allApplicants,
                        isCosigner,
                        applicationGroupHasTouredHome,
                        leaseInfo?.basicInfo?.status,
                      )}
                    />
                  </Box>
                  {leaseInfo ? (
                    <div
                      className="flex-1 px-sm md:pr-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ResidentListContainer
                        withStatus
                        residents={leaseInfo}
                        orientation="vertical"
                        signatureInformation={signatureInformation}
                      />
                    </div>
                  ) : (
                    <Flex flex={1} py="lg" justifyContent="center">
                      {isPrequalified ? (
                        <PlaceInLine
                          value={placeInLine}
                          qualifiedApplicants={listing?.waitlistRankingTotal}
                        />
                      ) : (
                        <ApplicationsInProcess
                          value={listing?.processingApplications}
                        />
                      )}
                    </Flex>
                  )}
                </Flex>
              </>
            ) : null}
          </>
        }
      />
      <WithdrawModal
        onHide={hideWithdrawModal}
        show={isWithdrawModalOpen}
        applicationId={bookmark.applicationId}
        unitId={basicInfo.unitId}
      />
    </>
  );
};

export default TourAndApps;
